// Organizer-only vendor directory. Search by keyword + city, filter by
// vendor category. Data comes from GET /api/vendors/browse.
import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, MapPin, X, Users2, Shield, Sparkles } from "lucide-react";
import { useVendorCategories, CATEGORY_LABEL } from "@/lib/vendorCategories";

function CategoryChip({ value, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`browse-vendors-cat-${value}`}
      aria-pressed={active}
      onClick={onClick}
      className="px-3 py-1.5 rounded-full border-2 text-sm transition"
      style={{
        borderColor: active ? "var(--terracotta)" : "var(--border-warm)",
        backgroundColor: active ? "rgba(201,67,42,0.10)" : "transparent",
        color: active ? "var(--terracotta)" : "var(--ink)",
      }}
    >
      {CATEGORY_LABEL[value] || value}
    </button>
  );
}

function VendorCard({ v }) {
  return (
    <Link
      to={v.slug ? `/vendor/${v.slug}` : `/vendors/${v.id}`}
      data-testid={`browse-vendor-card-${v.id}`}
      className="block rounded-xl border overflow-hidden hover:shadow-md transition"
      style={{
        borderColor: "rgba(0,0,0,0.08)",
        backgroundColor: "#fff",
      }}
    >
      <div
        className="aspect-[4/3] bg-cover bg-center"
        style={{
          backgroundColor: "var(--sand)",
          backgroundImage: v.portfolio_url ? `url(${v.portfolio_url})` : undefined,
        }}
      />
      <div className="p-3 space-y-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="font-medium truncate">
            {v.business_name || v.name || "Untitled maker"}
          </div>
          {v.ai_free && (
            <span
              className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
              title="This maker doesn't use AI"
              style={{
                backgroundColor: "rgba(31,93,52,0.08)",
                color: "var(--forest)",
              }}
            >
              AI-free
            </span>
          )}
          {v.nonprofit_status === "verified" && (
            <Shield
              size={13}
              className="shrink-0"
              style={{ color: "var(--forest)" }}
              aria-label="Verified nonprofit"
            />
          )}
        </div>
        {v.location && (
          <div
            className="text-xs flex items-center gap-1"
            style={{ color: "var(--ink-soft)" }}
          >
            <MapPin size={11} />
            {v.location}
          </div>
        )}
        {v.categories?.length ? (
          <div className="flex flex-wrap gap-1 pt-1">
            {v.categories.slice(0, 3).map((c) => (
              <span
                key={c}
                className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: "rgba(107,47,186,0.08)",
                  color: "var(--avt-purple)",
                }}
              >
                {CATEGORY_LABEL[c] || c}
              </span>
            ))}
          </div>
        ) : null}
        {v.bio && (
          <div
            className="text-xs pt-1 line-clamp-2"
            style={{ color: "var(--ink-soft)" }}
          >
            {v.bio}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function BrowseVendors() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const cats = useVendorCategories();

  const [selected, setSelected] = useState(() => {
    const c = searchParams.get("categories");
    return c ? c.split(",").filter(Boolean) : [];
  });
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [location, setLocation] = useState(searchParams.get("location") || "");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (selected.length) params.set("categories", selected.join(","));
      if (q.trim()) params.set("q", q.trim());
      if (location.trim()) params.set("location", location.trim());
      params.set("limit", "60");
      const { data } = await api.get(`/vendors/browse?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Could not load vendors right now."
      );
    } finally {
      setLoading(false);
    }
  }, [selected, q, location]);

  useEffect(() => {
    load();
  }, [load]);

  // Reflect current filters in the URL so a browse view is shareable.
  useEffect(() => {
    const next = {};
    if (selected.length) next.categories = selected.join(",");
    if (q.trim()) next.q = q.trim();
    if (location.trim()) next.location = location.trim();
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, q, location]);

  const toggle = (v) =>
    setSelected((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    );

  const clearAll = () => {
    setSelected([]);
    setQ("");
    setLocation("");
  };

  const isOrganizer = user?.role === "organizer" || user?.is_admin;

  return (
    <div
      className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12"
      data-testid="browse-vendors-page"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
          style={{
            backgroundColor: "rgba(107,47,186,0.10)",
            color: "var(--avt-purple)",
          }}
        >
          <Users2 size={22} />
        </div>
        <div className="flex-1">
          <div
            className="text-[10px] uppercase tracking-widest"
            style={{ color: "var(--terracotta)" }}
          >
            Find makers
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
            Browse the vendor directory
          </h1>
          <p
            className="text-sm mt-1"
            style={{ color: "var(--ink-soft)" }}
          >
            Filter every AddVendTour vendor by category, keyword, or city — then invite them from their profile.
          </p>
        </div>
      </div>

      {!isOrganizer ? (
        <div
          data-testid="browse-vendors-forbidden"
          className="mt-8 rounded-lg border p-6 text-sm"
          style={{
            borderColor: "rgba(0,0,0,0.08)",
            color: "var(--ink-soft)",
          }}
        >
          The vendor directory is available to organizers. Switch to an organizer account or upgrade your role from settings.
        </div>
      ) : (
        <>
          <div
            className="mt-6 rounded-lg border p-4 space-y-4"
            style={{
              borderColor: "rgba(0,0,0,0.08)",
              backgroundColor: "#fff",
            }}
          >
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "var(--ink-soft)" }}
                />
                <Input
                  data-testid="browse-vendors-q"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, brand, or bio"
                  className="pl-9"
                />
              </div>
              <div className="relative">
                <MapPin
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "var(--ink-soft)" }}
                />
                <Input
                  data-testid="browse-vendors-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="City or region"
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <div
                className="text-[11px] uppercase tracking-widest mb-2"
                style={{ color: "var(--ink-soft)" }}
              >
                Category
              </div>
              <div className="flex flex-wrap gap-2">
                {(cats.categories || []).map((c) => (
                  <CategoryChip
                    key={c.value}
                    value={c.value}
                    active={selected.includes(c.value)}
                    onClick={() => toggle(c.value)}
                  />
                ))}
              </div>
            </div>
            {(selected.length || q || location) ? (
              <div className="flex items-center justify-between text-sm">
                <div style={{ color: "var(--ink-soft)" }}>
                  {loading ? "Searching…" : (
                    <>
                      <strong data-testid="browse-vendors-total">{total}</strong>{" "}
                      matching {total === 1 ? "vendor" : "vendors"}
                    </>
                  )}
                </div>
                <button
                  type="button"
                  data-testid="browse-vendors-clear"
                  onClick={clearAll}
                  className="inline-flex items-center gap-1 text-sm underline"
                  style={{ color: "var(--terracotta)" }}
                >
                  <X size={13} /> Clear filters
                </button>
              </div>
            ) : (
              <div
                className="text-sm"
                style={{ color: "var(--ink-soft)" }}
              >
                <Sparkles size={13} className="inline mr-1" />
                Showing every AddVendTour vendor. Narrow it down with the filters above.
              </div>
            )}
          </div>

          {error && (
            <div
              data-testid="browse-vendors-error"
              className="mt-6 text-sm p-3 rounded-md"
              style={{
                backgroundColor: "rgba(201,67,42,0.08)",
                color: "var(--terracotta)",
              }}
            >
              {error}
            </div>
          )}

          <div
            className="mt-6 grid gap-4"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
          >
            {loading ? (
              <div
                data-testid="browse-vendors-loading"
                className="col-span-full py-12 flex items-center justify-center text-sm"
                style={{ color: "var(--ink-soft)" }}
              >
                <Loader2 size={14} className="animate-spin mr-2" />
                Loading vendors…
              </div>
            ) : items.length === 0 ? (
              <div
                data-testid="browse-vendors-empty"
                className="col-span-full py-16 text-center text-sm"
                style={{ color: "var(--ink-soft)" }}
              >
                No vendors match those filters. Try loosening the categories or clearing the location.
              </div>
            ) : (
              items.map((v) => <VendorCard key={v.id} v={v} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}
